import { Node } from '@tiptap/core';
import { HardBreak } from '@tiptap/extension-hard-break';

type SerializerState = {
  out: string;
  esc: (text: string, startOfLine?: boolean) => string;
  text: (text: string) => void;
  write: (content: string) => void;
  inTable?: boolean;
};

type Token = { content: string; map: [number, number] | null };

type InlineState = {
  src: string;
  pos: number;
  posMax: number;
  push: (type: string, tag: string, nesting: number) => Token;
};

type BlockState = {
  src: string;
  bMarks: number[];
  eMarks: number[];
  tShift: number[];
  sCount: number[];
  blkIndent: number;
  line: number;
  isEmpty: (line: number) => boolean;
  getLines: (begin: number, end: number, indent: number, keepLastLF: boolean) => string;
  push: (type: string, tag: string, nesting: number) => Token;
};

type MarkdownIt = {
  inline: { ruler: { at: (name: string, rule: (state: InlineState, silent: boolean) => boolean) => void } };
  block: {
    ruler: {
      at: (
        name: string,
        rule: (state: BlockState, startLine: number, endLine: number, silent: boolean) => boolean,
        options: { alt: string[] },
      ) => void;
    };
  };
};

// The only HTML this editor reads: what it writes (underline, a break in a markdown table cell, a table markdown
// can't express) and the `<p>` paragraphs prompts were saved as in May 2026. Prompts use `<name>` as a
// placeholder, so any other tag must read back as text.
const INLINE_TAG = String.raw`(?:\/?u|br\s*\/?)>`;
const BLOCK_TAG = String.raw`\/?(?:div|p|table)(?=[\s/>]|$)`;
const inlineTag = new RegExp(`^<${INLINE_TAG}`, 'i');
const blockTag = new RegExp(`^<${BLOCK_TAG}`, 'i');
const tagStart = new RegExp(`<(?=${INLINE_TAG}|${BLOCK_TAG})`, 'gi');

const htmlInline = (state: InlineState, silent: boolean) => {
  if (state.src[state.pos] !== '<') return false;
  const tag = inlineTag.exec(state.src.slice(state.pos, state.posMax))?.[0];
  if (!tag) return false;
  if (!silent) state.push('html_inline', '', 0).content = tag;
  state.pos += tag.length;
  return true;
};

// markdown-it's own html_block rule, narrowed to BLOCK_TAG.
const htmlBlock = (state: BlockState, startLine: number, endLine: number, silent: boolean) => {
  const start = state.bMarks[startLine] + state.tShift[startLine];
  if (!blockTag.test(state.src.slice(start, state.eMarks[startLine]))) return false;
  if (silent) return true;
  let nextLine = startLine + 1;
  while (nextLine < endLine && !state.isEmpty(nextLine) && state.sCount[nextLine] >= state.blkIndent) nextLine++;
  state.line = nextLine;
  const token = state.push('html_block', '', 0);
  token.map = [startLine, nextLine];
  token.content = state.getLines(startLine, nextLine, state.blkIndent, true);
  return true;
};

// tiptap-markdown escapes every [ and ] and writes < and > as &lt; and &gt;. Agents read this markdown raw,
// so only a bracket that could close a link and a < that the parser above reads as a tag keep an escape.
const unescapeBrackets = (escaped: string) => escaped.replace(/\\([[\]])(?![([:])/g, '$1');
const escapeTags = (escaped: string) => escaped.replace(tagStart, '\\<');

export const MarkdownText = Node.create({
  name: 'text',
  group: 'inline',

  addStorage() {
    return {
      markdown: {
        serialize(state: SerializerState, node: { text: string; marks: { type: { name: string } }[] }) {
          const { esc } = state;
          // In link text a lone bracket ends the label early, so every bracket there keeps its escape.
          const inLink = node.marks.some(mark => mark.type.name === 'link');
          // Text after a hard break starts a line, where `- `, `# ` or `> ` would open a block.
          const afterBreak = state.out.endsWith('\n');
          state.esc = (text, startOfLine) => {
            const escaped = escapeTags(esc.call(state, text, startOfLine || afterBreak));
            return inLink ? escaped : unescapeBrackets(escaped);
          };
          state.text(node.text);
          state.esc = esc;
        },
        parse: {
          setup(markdown: MarkdownIt) {
            markdown.inline.ruler.at('html_inline', htmlInline);
            markdown.block.ruler.at('html_block', htmlBlock, { alt: ['paragraph', 'reference', 'blockquote'] });
          },
        },
      },
    };
  },
});

// A plain newline reads back as this break only because NotionEditor parses with `breaks`; without it the
// newline collapses into a space.
export const MarkdownHardBreak = HardBreak.extend({
  addStorage() {
    return {
      markdown: {
        serialize(
          state: SerializerState,
          node: { type: unknown },
          parent: { childCount: number; child: (i: number) => { type: unknown } },
          index: number,
        ) {
          for (let i = index + 1; i < parent.childCount; i++) {
            if (parent.child(i).type !== node.type) {
              state.write(state.inTable ? '<br>' : '\n');
              return;
            }
          }
        },
      },
    };
  },
});
